/**
 * Medição de deriva entre duas máquinas de verdade.
 *
 * O Playwright numa máquina só não consegue medir isso: as duas abas leem o
 * mesmo relógio de sistema, então a diferença entre elas é zero por
 * construção. Aqui o servidor é o árbitro.
 *
 * A ideia: cada aba, ao passar por um cruzamento do centro, manda uma marca
 * dizendo que horas ela acha que são no relógio do servidor. O servidor
 * carimba a chegada no relógio dele. Com `e` sendo o erro de relógio da aba,
 * isto é, o quanto ela acha que o servidor está adiantado a mais do que
 * está de verdade:
 *
 *   sentAtBelieved = envioReal + e
 *   arrivalAt      = envioReal + atrasoDeSubida
 *   e ≈ sentAtBelieved - arrivalAt + rtt / 2
 *
 * A deriva visual entre as duas telas é `eA - eB`, porque cada aba desenha a
 * bolinha na posição correspondente ao tempo que ela acha que é.
 *
 * O que a conta assume: que o atraso de subida é metade do round-trip. É a
 * mesma hipótese do timesync, então ela não prova que o timesync acertou.
 * O que ela mede de verdade é o quanto as duas pontas discordam, e aí
 * roteamento torto de um lado só aparece. Um par isolado é ruidoso por causa
 * do jitter do próprio pacote de marca; o número pra olhar é a mediana.
 *
 * A marca carrega o instante do envio, não o do cruzamento teórico, de
 * propósito: senão o atraso entre o cruzamento e o envio entraria em `e`
 * como se fosse erro de relógio.
 */
import { z } from 'zod'
import { roleSchema, roomIdSchema } from './schemas.js'
import { median } from './timesync/stat.js'

export const measureMarkSchema = z.object({
  /** Identifica a série, pra não parear marcas de séries diferentes. */
  setStartAt: z.number(),
  /** Qual cruzamento do centro. É a chave do pareamento entre as duas pontas. */
  passIndex: z.number().int().min(0),
  /** Cruzamento teórico no relógio do servidor. Igual dos dois lados. */
  believedCrossingAt: z.number(),
  /** Que horas a aba achava que eram no servidor na hora exata do envio. */
  sentAtBelieved: z.number(),
  /** Offset que a aba estava usando. */
  offset: z.number(),
  /** Mediana de round-trip da última rodada de sync, em ms. */
  rtt: z.number().min(0),
})

export const storedMarkSchema = measureMarkSchema.extend({
  role: roleSchema,
  /** Relógio do servidor quando a marca chegou. */
  arrivalAt: z.number(),
})

export const measureReportSchema = z.object({
  roomId: roomIdSchema,
  role: roleSchema,
  /** Como chamar esse aparelho no relatório. */
  label: z.string().max(80),
  userAgent: z.string().max(300),
  rounds: z
    .array(
      z.object({
        at: z.number(),
        offset: z.number(),
        medianRoundtrip: z.number(),
        offsetSpread: z.number(),
      }),
    )
    .max(200),
})

export type MeasureMark = z.infer<typeof measureMarkSchema>
export type StoredMark = z.infer<typeof storedMarkSchema>
export type MeasureReport = z.infer<typeof measureReportSchema>

/** Erro de relógio estimado dessa aba, em ms. Positivo é aba adiantada. */
export function clockError(mark: StoredMark): number {
  return mark.sentAtBelieved - mark.arrivalAt + mark.rtt / 2
}

export interface DriftPair {
  setStartAt: number
  passIndex: number
  believedCrossingAt: number
  therapistError: number
  patientError: number
  /** Deriva visual estimada. Positivo é terapeuta adiantada. */
  driftMs: number
  /** A mesma diferença sem a correção de rtt/2, pra comparar. */
  rawDriftMs: number
}

export interface DriftSummary {
  samples: number
  medianAbs: number
  maxAbs: number
  p95Abs: number
  /** Viés com sinal: mostra se uma ponta anda sempre à frente da outra. */
  medianSigned: number
}

const key = (mark: { setStartAt: number; passIndex: number }): string =>
  `${mark.setStartAt}:${mark.passIndex}`

function percentile(sorted: readonly number[], p: number): number {
  if (sorted.length === 0) return 0
  const index = Math.min(sorted.length - 1, Math.ceil((p / 100) * sorted.length) - 1)
  return sorted[Math.max(0, index)] as number
}

/** Pareia as marcas das duas pontas por série e cruzamento. */
export function pairMarks(marks: readonly StoredMark[]): DriftPair[] {
  const therapist = new Map<string, StoredMark>()
  const patient = new Map<string, StoredMark>()
  for (const mark of marks) {
    const target = mark.role === 'therapist' ? therapist : patient
    // Se a mesma marca chegar duas vezes, a primeira vale: é a que não
    // passou por retransmissão.
    if (!target.has(key(mark))) target.set(key(mark), mark)
  }

  const pairs: DriftPair[] = []
  for (const [id, a] of therapist) {
    const b = patient.get(id)
    if (b === undefined) continue
    const therapistError = clockError(a)
    const patientError = clockError(b)
    pairs.push({
      setStartAt: a.setStartAt,
      passIndex: a.passIndex,
      believedCrossingAt: a.believedCrossingAt,
      therapistError,
      patientError,
      driftMs: therapistError - patientError,
      rawDriftMs: a.sentAtBelieved - a.arrivalAt - (b.sentAtBelieved - b.arrivalAt),
    })
  }

  pairs.sort((x, y) => x.believedCrossingAt - y.believedCrossingAt)
  return pairs
}

export function summarize(pairs: readonly DriftPair[]): DriftSummary | null {
  if (pairs.length === 0) return null
  const abs = pairs.map((p) => Math.abs(p.driftMs)).sort((a, b) => a - b)
  return {
    samples: pairs.length,
    medianAbs: median(abs),
    maxAbs: abs[abs.length - 1] as number,
    p95Abs: percentile(abs, 95),
    medianSigned: median(pairs.map((p) => p.driftMs)),
  }
}
